import { useState, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import {
  Activity,
  MapPin,
  Clock,
  Phone,
  MessageCircle,
  Navigation,
  AlertCircle,
  CheckCircle,
  XCircle
} from 'lucide-react';
import { TabLayout, ContentCard } from '../../components/ui/TabLayout';
import { TripCard } from '../../components/trips/TripCard';
import { TripDetailsModal } from '../../components/trips/TripDetailsModal';
import { ActiveTripMonitor, ActiveTripsSummary } from '../../components/trips/ActiveTripMonitor';
import { TRIP_STATUS, updateTripLocation, completeTrip, cancelTrip } from '../../store/tripSlice';

export function ActiveTripsPage() {
  const dispatch = useDispatch();
  const trips = useSelector(state => state.trips.trips);
  const [activeTab, setActiveTab] = useState('ongoing');
  const [selectedTrip, setSelectedTrip] = useState(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);

  // Filter trips by status
  const ongoingTrips = useMemo(() => 
    trips.filter(trip => trip.status === TRIP_STATUS.ONGOING), [trips]
  );
  
  const assignedTrips = useMemo(() => 
    trips.filter(trip => trip.status === TRIP_STATUS.ASSIGNED), [trips]
  );
  
  const requestedTrips = useMemo(() => 
    trips.filter(trip => trip.status === TRIP_STATUS.REQUESTED), [trips]
  );

  const tabs = [
    { 
      id: 'ongoing', 
      label: `Ongoing (${ongoingTrips.length})`, 
      icon: Activity 
    },
    { 
      id: 'assigned', 
      label: `Assigned (${assignedTrips.length})`, 
      icon: CheckCircle 
    },
    { 
      id: 'requested', 
      label: `Requested (${requestedTrips.length})`, 
      icon: AlertCircle 
    }
  ];

  const handleViewDetails = (trip) => {
    setSelectedTrip(trip);
    setShowDetailsModal(true);
  };

  const handleCompleteTrip = (tripId) => {
    dispatch(completeTrip({ tripId }));
  };

  const handleCancelTrip = (tripId, reason = 'Cancelled by admin') => {
    dispatch(cancelTrip({ tripId, reason }));
  };

  const handleRefresh = () => {
    // In a real app, this would trigger a data refresh from the API
    console.log('Refreshing active trips data...');
  };

  const ActiveTripCard = ({ trip, showActions = true }) => {
    const getStatusBadge = (status) => {
      const statusConfig = {
        [TRIP_STATUS.ONGOING]: { variant: 'info', label: 'In Progress' },
        [TRIP_STATUS.ASSIGNED]: { variant: 'warning', label: 'Assigned' },
        [TRIP_STATUS.REQUESTED]: { variant: 'secondary', label: 'Requested' }
      };
      
      const config = statusConfig[status] || { variant: 'secondary', label: status };
      return <Badge variant={config.variant}>{config.label}</Badge>;
    };

    const formatTime = (dateString) => {
      return new Date(dateString).toLocaleTimeString('en-US', {
        hour: '2-digit',
        minute: '2-digit'
      });
    };

    const getElapsedTime = (startTime) => {
      const elapsed = Math.floor((new Date() - new Date(startTime)) / 60000);
      return `${elapsed} min ago`;
    };

    return (
      <Card className="p-6 hover:shadow-md transition-shadow">
        <div className="flex items-start justify-between mb-4">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
              <Navigation className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <h3 className="font-semibold text-gray-900">Trip #{trip.id.slice(-6)}</h3>
              <p className="text-sm text-gray-500">
                {trip.requestedAt && formatTime(trip.requestedAt)}
                {trip.startedAt && ` • Started ${getElapsedTime(trip.startedAt)}`}
              </p>
            </div>
          </div>
          {getStatusBadge(trip.status)}
        </div>

        {/* Route Information */}
        <div className="space-y-3 mb-4">
          <div className="flex items-start space-x-3">
            <div className="w-3 h-3 rounded-full bg-green-500 mt-1"></div>
            <div className="flex-1">
              <p className="text-sm font-medium text-gray-900">{trip.pickup.name}</p>
              <p className="text-xs text-gray-500">{trip.pickup.address}</p>
            </div>
          </div>
          <div className="flex items-start space-x-3">
            <div className="w-3 h-3 rounded-full bg-red-500 mt-1"></div>
            <div className="flex-1">
              <p className="text-sm font-medium text-gray-900">{trip.dropoff.name}</p>
              <p className="text-xs text-gray-500">{trip.dropoff.address}</p>
            </div>
          </div>
        </div>

        {/* Trip Details */}
        <div className="grid grid-cols-2 gap-4 mb-4 p-3 bg-gray-50 rounded-lg">
          <div>
            <p className="text-xs text-gray-500">Passenger</p>
            <p className="text-sm font-medium text-gray-900">{trip.passenger.name}</p>
          </div>
          {trip.driver && (
            <div>
              <p className="text-xs text-gray-500">Driver</p>
              <p className="text-sm font-medium text-gray-900">{trip.driver.name}</p>
            </div>
          )}
          <div>
            <p className="text-xs text-gray-500">Distance</p>
            <p className="text-sm font-medium text-gray-900">{trip.distance} km</p>
          </div>
          <div>
            <p className="text-xs text-gray-500">Fare</p>
            <p className="text-sm font-medium text-gray-900">${trip.fare.total}</p>
          </div>
        </div>

        {/* Action Buttons */}
        {showActions && (
          <div className="flex space-x-2">
            {trip.status === TRIP_STATUS.ONGOING && (
              <Button
                variant="primary"
                size="sm"
                onClick={() => handleCompleteTrip(trip.id)}
                className="flex-1"
              >
                <CheckCircle className="w-4 h-4 mr-1" />
                Complete Trip
              </Button>
            )}
            {[TRIP_STATUS.REQUESTED, TRIP_STATUS.ASSIGNED].includes(trip.status) && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => handleCancelTrip(trip.id)}
                className="flex-1"
              >
                <XCircle className="w-4 h-4 mr-1" />
                Cancel
              </Button>
            )}
            <Button variant="outline" size="sm">
              <Phone className="w-4 h-4" />
            </Button>
            <Button variant="outline" size="sm">
              <MessageCircle className="w-4 h-4" />
            </Button>
          </div>
        )}
      </Card>
    );
  };

  const renderTabContent = () => {
    let tripsToShow = [];

    switch (activeTab) {
      case 'ongoing':
        tripsToShow = ongoingTrips;
        break;
      case 'assigned':
        tripsToShow = assignedTrips;
        break;
      case 'requested':
        tripsToShow = requestedTrips;
        break;
      default:
        tripsToShow = ongoingTrips;
    }

    // Use ActiveTripMonitor for ongoing trips
    if (activeTab === 'ongoing') {
      return (
        <ActiveTripMonitor
          trips={tripsToShow}
          onViewDetails={handleViewDetails}
          onCompleteTrip={handleCompleteTrip}
          onCancelTrip={handleCancelTrip}
          onRefresh={handleRefresh}
        />
      );
    }

    // Standard display for other tabs
    if (tripsToShow.length === 0) {
      return (
        <ContentCard>
          <div className="text-center py-12">
            <Activity className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              No {activeTab} trips
            </h3>
            <p className="text-gray-500">
              {activeTab === 'assigned' && 'No trips are waiting to be started.'}
              {activeTab === 'requested' && 'No new trip requests at the moment.'}
            </p>
          </div>
        </ContentCard>
      );
    }

    return (
      <div className="space-y-6">
        {/* Summary for assigned/requested trips */}
        <ActiveTripsSummary trips={tripsToShow} />

        <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6">
          {tripsToShow.map((trip) => (
            <TripCard
              key={trip.id}
              trip={trip}
              onViewDetails={handleViewDetails}
              onCompleteTrip={handleCompleteTrip}
              onCancelTrip={handleCancelTrip}
            />
          ))}
        </div>
      </div>
    );
  };

  return (
    <TabLayout
      title="Active Trips"
      subtitle="Monitor and manage ongoing trips in real-time"
      tabs={tabs}
      activeTab={activeTab}
      onTabChange={setActiveTab}
    >
      {renderTabContent()}

      {/* Trip Details Modal */}
      <TripDetailsModal
        trip={selectedTrip}
        isOpen={showDetailsModal}
        onClose={() => {
          setShowDetailsModal(false);
          setSelectedTrip(null);
        }}
        onCompleteTrip={handleCompleteTrip}
        onCancelTrip={handleCancelTrip}
      />
    </TabLayout>
  );
}
